import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  cn,
  toast,
} from "@cliently/ui";
import { selectCompletableSessionSteps } from "@cliently/schemas";
import {
  isStepCompleted,
  useUpdateAssignmentSessionNoteClient,
  useUpdateAssignmentSessionStatusClient,
  useUpdateProgramAssignmentStep,
  type PortalAssignmentSession,
  type PortalProgramSessionStatus,
} from "../../hooks/use-programs";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: PortalAssignmentSession | null;
  clientId: string;
  clientNotesEnabled: boolean;
};

const statusBadge = (status: PortalProgramSessionStatus) => {
  switch (status) {
    case "DONE":
      return "bg-emerald-500/15 text-emerald-600 border-emerald-500/40";
    case "MISSED":
      return "bg-rose-600/15 text-rose-600 border-rose-600/40";
    case "PARTIAL":
      return "bg-amber-500/15 text-amber-600 border-amber-500/40";
    case "PENDING":
    default:
      return "bg-muted text-muted-foreground border-border/70";
  }
};

const statusLabel = (
  status: PortalProgramSessionStatus,
  t: (k: string, d: string) => string,
) => {
  switch (status) {
    case "DONE":
      return t("programs.statusDone", "Complète");
    case "MISSED":
      return t("programs.statusMissed", "Manquée");
    case "PARTIAL":
      return t("programs.statusPartial", "Partielle");
    case "PENDING":
    default:
      return t("programs.statusPending", "À venir");
  }
};

const Textarea = ({
  className,
  rows,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    rows={rows ?? 3}
    className={cn(
      "border-border/70 bg-background placeholder:text-muted-foreground focus-visible:ring-primary/40 flex w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...rest}
  />
);

export const ClientSessionDetailModal = ({
  open,
  onOpenChange,
  session,
  clientId,
  clientNotesEnabled,
}: Props) => {
  const { t, i18n } = useTranslation();
  const updateStep = useUpdateProgramAssignmentStep();
  const updateNote = useUpdateAssignmentSessionNoteClient();
  const updateStatus = useUpdateAssignmentSessionStatusClient();

  const [clientNote, setClientNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (session) {
      setClientNote(session.clientNote ?? "");
    }
  }, [session?.id, session]);

  const checkboxes = useMemo(
    () =>
      session
        ? selectCompletableSessionSteps([...session.steps])
            .filter((s) => s.type === "CHECKBOX")
            .sort((a, b) => a.position - b.position)
        : [],
    [session],
  );

  const allDone = checkboxes.length > 0 && checkboxes.every((s) => s.isDone);
  const anyDone = checkboxes.some((s) => s.isDone);

  if (!session) return null;

  const handleToggle = (stepId: string, isDone: boolean) => {
    updateStep.mutate({
      id: stepId,
      data: { isDone: !isDone },
      assignmentClientId: clientId,
    });
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      await updateNote.mutateAsync({
        sessionId: session.id,
        clientId,
        clientNote: clientNote.trim() ? clientNote.trim() : null,
      });
      toast.success(t("programs.noteSaved", "Note enregistrée."));
    } catch {
      toast.error(t("programs.noteSaveError", "Impossible d'enregistrer la note."));
    } finally {
      setSavingNote(false);
    }
  };

  const handleValidateSession = async () => {
    if (!anyDone) {
      toast.error(
        t(
          "programs.validateAtLeastOne",
          "Cochez au moins un exercice avant de valider.",
        ),
      );
      return;
    }
    const target: PortalProgramSessionStatus = allDone ? "DONE" : "PARTIAL";
    try {
      await updateStatus.mutateAsync({
        sessionId: session.id,
        clientId,
        status: target,
      });
      toast.success(
        target === "DONE"
          ? t("programs.sessionValidated", "Séance validée — Bravo !")
          : t(
              "programs.sessionValidatedPartial",
              "Séance enregistrée en partielle.",
            ),
      );
    } catch {
      toast.error(
        t(
          "programs.sessionValidateError",
          "Impossible de valider la séance pour le moment.",
        ),
      );
    }
  };

  const dateLine = (() => {
    const opt: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "long",
      year: "numeric",
    };
    if (session.completedAt) {
      return t("programs.sessionValidatedOn", "Séance validée le {{date}}", {
        date: new Date(session.completedAt).toLocaleDateString(
          i18n.language,
          opt,
        ),
      });
    }
    if (session.missedAt) {
      return t("programs.sessionMissedOn", "Séance manquée le {{date}}", {
        date: new Date(session.missedAt).toLocaleDateString(i18n.language, opt),
      });
    }
    if (session.scheduledAt) {
      return t("programs.sessionScheduledOn", "Séance prévue le {{date}}", {
        date: new Date(session.scheduledAt).toLocaleDateString(
          i18n.language,
          opt,
        ),
      });
    }
    return t("programs.sessionUnscheduled", "Séance non datée");
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="w-[calc(100vw-1.5rem)] max-w-xl sm:w-full"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="truncate text-base font-bold">
                {session.title}
              </DialogTitle>
              <p className="text-muted-foreground mt-0.5 text-xs">{dateLine}</p>
            </div>
            <Badge
              variant="outline"
              className={cn("shrink-0 border", statusBadge(session.status))}
            >
              {statusLabel(session.status, t)} · {session.progressPercent}%
            </Badge>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {session.description && (
            <p className="text-muted-foreground text-xs">
              {session.description}
            </p>
          )}

          {/* Coach note (read-only) */}
          {session.coachNote?.trim() && (
            <div className="bg-muted/20 border-primary/50 rounded-md border-l-4 p-3 text-sm">
              <p className="text-foreground/70 mb-1 text-[10px] font-bold uppercase tracking-[0.1em]">
                {t("programs.coachNote", "Note du coach")}
              </p>
              <p className="whitespace-pre-wrap">{session.coachNote}</p>
            </div>
          )}

          {/* Exercises checklist */}
          {checkboxes.length > 0 ? (
            <div className="space-y-1.5">
              <Label className="text-xs">
                {t("programs.exercisesChecklistLabel", "Exercices")}
              </Label>
              <ul className="divide-border/40 divide-y rounded-md border">
                {checkboxes.map((step) => (
                  <li
                    key={step.id}
                    className="flex items-center gap-3 px-3 py-2 text-sm"
                  >
                    <Checkbox
                      checked={step.isDone}
                      onCheckedChange={() =>
                        handleToggle(step.id, step.isDone)
                      }
                      className="shrink-0"
                    />
                    <span
                      className={cn(
                        "min-w-0 flex-1 truncate",
                        step.isDone &&
                          "text-muted-foreground line-through",
                      )}
                    >
                      {step.exercise?.nameFr?.trim() ||
                        step.exercise?.name ||
                        step.title}
                    </span>
                    {step.targetValue != null && (
                      <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                        {step.targetValue} {step.targetUnit ?? ""}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-muted-foreground text-xs italic">
              {t(
                "programs.noExercisesYet",
                "Aucun exercice n'a été ajouté à cette séance.",
              )}
            </p>
          )}

          {/* Client note */}
          <div className="space-y-1.5">
            <Label className="text-xs">
              {t("programs.clientNote", "Note du client")}
              {!clientNotesEnabled && (
                <span className="text-muted-foreground ml-2 font-normal">
                  ({t("programs.notesDisabled", "désactivées par votre coach")})
                </span>
              )}
            </Label>
            {clientNotesEnabled ? (
              <Textarea
                value={clientNote}
                onChange={(e) => setClientNote(e.target.value)}
                rows={3}
                placeholder={t(
                  "programs.clientNotePlaceholderClient",
                  "Comment s'est passée la séance ? (durée, ressenti, ajustements…)",
                )}
              />
            ) : session.clientNote?.trim() ? (
              <p className="bg-muted/30 rounded-md p-3 text-sm">
                {session.clientNote}
              </p>
            ) : (
              <p className="text-muted-foreground text-xs italic">
                {t(
                  "programs.noClientNoteYet",
                  "Vous n'avez pas laissé de note pour cette séance.",
                )}
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close", "Fermer")}
          </Button>
          <div className="flex-1" />
          {clientNotesEnabled && (
            <Button
              variant="outline"
              onClick={handleSaveNote}
              disabled={savingNote}
            >
              {savingNote
                ? t("common.saving", "Enregistrement…")
                : t("programs.saveClientNote", "Enregistrer ma note")}
            </Button>
          )}
          {session.status !== "DONE" && checkboxes.length > 0 && (
            <Button
              onClick={handleValidateSession}
              disabled={updateStatus.isPending}
            >
              {updateStatus.isPending
                ? t("common.saving", "Enregistrement…")
                : t("programs.validateSession", "Valider la séance")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Keep helper referenced to please TS unused checks if needed elsewhere.
void isStepCompleted;
